"use strict";
/**
 * Derive a Polymarket US sport slug (the `tagSlug` its /events endpoint takes,
 * e.g. "mlb", "nba", "cfb") from identifiers we already hold for a market.
 *
 * Pure and DOM/state-free so it can be unit-tested and reused. Two inputs:
 *
 *   - any Kalshi ticker string with a league stem as its PREFIX — a series
 *     ticker, event ticker, or market ticker all work (`KXMLBGAME`,
 *     `KXMLBGAME-26AUG14STLCHC`, `KXNCAAFHIT-…`), and
 *   - a Polymarket US event `seriesSlug` (`mlb-2026`).
 *
 * Deliberately NOT accepted: Kalshi's `Event.category` field. Despite the
 * name, it is not a league identifier — it's Kalshi's broad content taxonomy
 * (`"Sports"`, `"Politics"`, `"Crypto"`, …; see markets/constants.ts
 * MARKET_CATEGORIES), the same for every sport. Feeding it here silently
 * yields `undefined` for every Kalshi market, which is exactly the bug this
 * module's callers must avoid — use an actual ticker.
 *
 * Both functions return `undefined` when nothing maps, letting callers fall
 * back rather than guess. The only league-abbreviation knowledge in the app
 * lives here.
 *
 * @module markets/pm-us-sport-slug
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.kalshiTickerToPmUsSlug = kalshiTickerToPmUsSlug;
exports.kalshiTickerToLeaguePrefix = kalshiTickerToLeaguePrefix;
exports.pmUsSlugToKalshiLeaguePrefix = pmUsSlugToKalshiLeaguePrefix;
exports.pmUsSeriesSlugToSportSlug = pmUsSeriesSlugToSportSlug;
const const_1 = require("../const");
/**
 * Kalshi league stems whose PM-US slug is NOT just the lowercased stem.
 * Kalshi tags college leagues `KXNCAA…`; Polymarket US uses short forms.
 * Everything else (KXMLB→mlb, KXNBA→nba, KXNFL→nfl, KXNHL→nhl, KXMLS→mls,
 * KXWNBA→wnba) needs no entry.
 */
const KALSHI_STEM_TO_PM_US_SLUG = {
    KXNCAAF: "cfb", // College football
    KXNCAAMB: "cbb", // College basketball (men)
    KXNCAAWB: "wcbb", // College basketball (women)
};
/**
 * PM-US sport slug for a Kalshi ticker string, or `undefined` when it matches
 * no known league (custom prefixes, unrecognised sports).
 *
 * `ticker` may be a series ticker, event ticker, or market ticker — anything
 * that starts with a league stem, since the match is a prefix test against
 * the same {@link KALSHI_LEAGUE_PREFIXES} stems the events page uses (so the
 * two never drift). That means any market-type suffix or trailing event/date
 * segment works unchanged: `KXMLBGAME`, `KXMLBHIT`, `KXMLBGAME-26AUG14STLCHC`
 * all resolve to "mlb". It must NOT be Kalshi's `Event.category` field — see
 * the module doc for why that always fails silently.
 *
 * Note the derived slug is a candidate only — a Kalshi league without a PM-US
 * counterpart (e.g. college baseball) yields a slug PM-US doesn't list; callers
 * validate against the live sports list before using it.
 */
function kalshiTickerToPmUsSlug(ticker) {
    const prefix = kalshiTickerToLeaguePrefix(ticker);
    if (!prefix)
        return undefined;
    return (KALSHI_STEM_TO_PM_US_SLUG[prefix] ??
        prefix.replace(/^KX/, "").toLowerCase());
}
/**
 * Kalshi league PREFIX for a ticker string (`KXMLBGAME-…` → `"KXMLB"`), or
 * `undefined` when it matches no known league. Same prefix test as
 * {@link kalshiTickerToPmUsSlug}, but returns the Kalshi-side stem instead of
 * the PM-US slug — the identifier the step-2 series presets are keyed on. As
 * with the PM-US variant, it must be a ticker, never `Event.category`.
 */
function kalshiTickerToLeaguePrefix(ticker) {
    if (!ticker)
        return undefined;
    const upper = ticker.toUpperCase();
    return const_1.KALSHI_LEAGUE_PREFIXES.find((p) => upper.startsWith(p.value))?.value;
}
/**
 * Reverse of {@link kalshiTickerToPmUsSlug}: the Kalshi league prefix for a
 * PM-US sport slug (`"mlb"` → `"KXMLB"`, `"cfb"` → `"KXNCAAF"`), or `undefined`
 * for a sport Kalshi doesn't list. Lets a PM-US primary market drive the
 * Kalshi-side series presets. Derived from the same
 * {@link KALSHI_LEAGUE_PREFIXES} + override table so the forward and reverse
 * maps can never disagree.
 */
function pmUsSlugToKalshiLeaguePrefix(slug) {
    const s = slug?.trim().toLowerCase();
    if (!s)
        return undefined;
    return PM_US_SLUG_TO_KALSHI_PREFIX.get(s);
}
const PM_US_SLUG_TO_KALSHI_PREFIX = new Map(const_1.KALSHI_LEAGUE_PREFIXES.map((p) => [
    KALSHI_STEM_TO_PM_US_SLUG[p.value] ??
        p.value.replace(/^KX/, "").toLowerCase(),
    p.value,
]));
/**
 * PM-US sport slug from an event `seriesSlug` by stripping a trailing season
 * year (`mlb-2026` → "mlb"). Returns `undefined` for an empty input. A slug
 * without a `-YYYY` suffix is returned as-is (already a bare sport slug).
 */
function pmUsSeriesSlugToSportSlug(seriesSlug) {
    const slug = seriesSlug?.trim().toLowerCase();
    if (!slug)
        return undefined;
    return slug.replace(/-\d{4}$/, "") || undefined;
}
